import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { RealTimeAnalyticsEvent } from '../../types/analytics';
import { ChevronRightIcon } from '@heroicons/react/24/solid';
import { EASE_OUT } from '@/lib/motion';
import { phantomProps } from '@/config/phantom';
import { useTheme } from '@/contexts/ThemeContext';

const STATUS_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  course_started: {
    bg: 'bg-primary-500/16',
    text: 'text-primary-700 dark:text-primary-400',
    label: 'Started',
  },
  course_completed: {
    bg: 'bg-success-500/16',
    text: 'text-success-700 dark:text-success-400',
    label: 'Finished',
  },
  prize_claimed: {
    bg: 'bg-warning-500/16',
    text: 'text-warning-700 dark:text-warning-400',
    label: 'Prize claimed',
  },
  module_started: {
    bg: 'bg-secondary-500/16',
    text: 'text-secondary-700 dark:text-secondary-400',
    label: 'In Progress',
  },
  feedback_submitted: {
    bg: 'bg-primary-500/16',
    text: 'text-primary-700 dark:text-primary-400',
    label: 'Done',
  },
  course_assigned: {
    bg: 'bg-grey-500/16',
    text: 'text-grey-700 dark:text-grey-400',
    label: 'Assigned',
  },
  deadline_missed: {
    bg: 'bg-error-500/16',
    text: 'text-error-700 dark:text-error-400',
    label: 'End Date Missed',
  },
};

const DEFAULT_STATUS = {
  bg: 'bg-grey-500/16',
  text: 'text-grey-700 dark:text-grey-400',
  label: 'Unknown',
};

interface RealTimeActivityTableProps {
  events: RealTimeAnalyticsEvent[];
  loading?: boolean;
}

const PLACEHOLDER_EVENTS: RealTimeAnalyticsEvent[] = Array.from({ length: 5 }, (_, i) => ({
  user: 'Loading learner name',
  course: 'Loading course title',
  event: 'module_started',
  timestamp: String(i),
}));

const RealTimeActivityTable: React.FC<RealTimeActivityTableProps> = ({ events, loading }) => {
  const { theme } = useTheme();
  const displayEvents = loading && events.length === 0 ? PLACEHOLDER_EVENTS : events;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: 0.1, ease: EASE_OUT }}
      className="h-full flex flex-col bg-white dark:bg-grey-900 rounded-2xl shadow overflow-hidden"
      data-testid="activity-feed"
    >
      {/* Header */}
      <div className="px-6 py-6">
        <h2 className="text-lg font-semibold text-text-primary">Analytics in Real Time</h2>
      </div>

      {/* Table */}
      <phantom-ui {...phantomProps(theme === 'dark', loading)}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead data-shimmer-ignore>
              <tr className="bg-neutral-100 dark:bg-grey-800">
                <th className="text-left text-sm font-semibold text-text-secondary p-4 whitespace-nowrap">
                  Learner
                </th>
                <th className="text-left text-sm font-semibold text-text-secondary p-4 whitespace-nowrap">
                  Event
                </th>
                <th className="text-left text-sm font-semibold text-text-secondary p-4 whitespace-nowrap">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {displayEvents.map((event, index) => {
                const status = STATUS_STYLES[event.event] || DEFAULT_STATUS;
                return (
                  <tr
                    key={`${event.timestamp}-${index}`}
                    className="border-b border-dashed border-border/20 last:border-b-0"
                  >
                    <td className="p-4 text-sm text-text-primary truncate max-w-[200px]">
                      {event.user}
                    </td>
                    <td className="p-4 text-sm text-text-primary truncate max-w-[200px]">
                      {event.course}
                    </td>
                    <td className="p-4">
                      <span
                        className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-xs font-bold ${status.bg} ${status.text}`}
                      >
                        {status.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </phantom-ui>

      {/* Empty state — fills the space left when the card stretches to match its neighbor */}
      {displayEvents.length === 0 && (
        <div className="flex-1 flex items-center justify-center p-8 text-sm text-text-tertiary">
          No recent activity
        </div>
      )}

      {/* Footer */}
      {events.length > 0 && (
        <div className="mt-auto flex justify-end px-4 py-4">
          <Link
            to="/analytics"
            className="inline-flex items-center gap-1 text-sm font-bold text-text-primary hover:text-primary-500"
            style={{ transition: 'color 120ms' }}
          >
            View all
            <ChevronRightIcon className="w-5 h-5" />
          </Link>
        </div>
      )}
    </motion.div>
  );
};

export default RealTimeActivityTable;
